// The Overview's one answer (the engine's /api/overview), at once (owner, 1 Oct 2026: "اول ما افتح الصفحة يظهر
// فورا"): the last answer kept in this browser is shown the moment the page opens, and the fresh one replaces it
// as soon as the engine answers (a minute's copy there - no Ejar call while the page waits).
import { useCallback, useEffect, useRef, useState } from 'react';
import { finesService, errorMessage } from '../../services/finesService';

const KEY = 'yelo.overview.v1';

const keyOf = ({ from, to, branch }) => `${KEY}:${from}:${to}:${branch || 'All'}`;

const readKept = (key) => {
    try {
        const kept = localStorage.getItem(key);
        return kept ? JSON.parse(kept) : null;
    } catch (e) {
        return null;          // storage blocked or a broken copy: the engine answers
    }
};

const keep = (key, data) => {
    try {
        // only the latest periods stay: older copies are dropped before a new one is written
        Object.keys(localStorage).filter((k) => k.startsWith(`${KEY}:`) && k !== key).slice(0, -5)
            .forEach((k) => localStorage.removeItem(k));
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) { /* storage full or blocked: nothing kept, the page still shows the answer */ }
};

/** { data, error, loading, kept, refresh } for a period and a branch. `kept` = what shows is the copy from before. */
export default function useOverview({ from, to, branch = 'All' }) {
    const key = keyOf({ from, to, branch });
    const [state, setState] = useState(() => ({ data: readKept(key), kept: true, error: '', loading: true }));
    const asked = useRef(0);

    const load = useCallback((fresh = false) => {
        const n = ++asked.current;
        setState((s) => ({ ...s, loading: true }));
        finesService.overview({ from, to, branch, fresh })
            .then((data) => {
                if (n !== asked.current) return;         // a newer question was asked meanwhile
                keep(key, data);
                setState({ data, kept: false, error: '', loading: false });
            })
            .catch((e) => {
                if (n !== asked.current) return;
                setState((s) => ({ ...s, error: errorMessage(e), loading: false }));
            });
    }, [from, to, branch, key]);

    useEffect(() => {
        setState({ data: readKept(key), kept: true, error: '', loading: true });
        load(false);
    }, [key, load]);

    return { ...state, refresh: () => load(true) };
}
